/**
 * Settings Component
 * 
 * Allows users to manage their privacy settings, block users, and view their profile.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { updateUserSettings, getUserById, blockUser, reportUser } from '../services/db';

const Settings = ({ onClose }) => {
  const { currentUser, userProfile, refreshProfile } = useAuth();
  const [settings, setSettings] = useState({
    messagePermissions: 'everyone',
    blockedUsers: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load current settings
  useEffect(() => {
    if (userProfile) {
      setSettings({
        messagePermissions: userProfile.messagePermissions || 'everyone',
        blockedUsers: userProfile.blockedUsers || [],
      });
    }
  }, [userProfile]);

  /**
   * Handle settings update
   */
  const handleUpdateSettings = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await updateUserSettings(currentUser.uid, settings);
      await refreshProfile();
      setSuccess('Settings updated successfully!');
    } catch (err) {
      console.error('Error updating settings:', err);
      setError('Failed to update settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle blocking a user
   */
  const handleBlockUser = async (userId) => {
    try {
      await blockUser(currentUser.uid, userId);
      await refreshProfile();
      setSuccess('User blocked successfully.');
    } catch (error) {
      console.error('Error blocking user:', error);
      setError('Failed to block user.');
    }
  };

  /**
   * Handle reporting a user
   */
  const handleReportUser = async (userId, reason) => {
    try {
      await reportUser(currentUser.uid, userId, reason);
      setSuccess('User reported successfully.');
    } catch (error) {
      console.error('Error reporting user:', error);
      setError('Failed to report user.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Profile Info */}
          <div className="text-center">
            <img
              src={userProfile?.avatarURL || '/default-avatar.png'}
              alt={userProfile?.displayName}
              className="w-20 h-20 rounded-full mx-auto mb-4"
            />
            <h3 className="text-lg font-semibold text-gray-800">
              {userProfile?.displayName}
            </h3>
            <p className="text-sm text-gray-600">{userProfile?.verifiedTag}</p>
          </div>

          {/* Success/Error Messages */}
          {success && (
            <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
              {success}
            </div>
          )}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Privacy Settings */}
          <div>
            <h4 className="text-lg font-semibold text-gray-800 mb-3">Privacy Settings</h4>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Who can send me messages?
                </label>
                <select
                  value={settings.messagePermissions}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    messagePermissions: e.target.value
                  }))}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="everyone">Everyone</option>
                  <option value="subscribers">Subscribers only (coming soon)</option>
                  <option value="none">No one</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Choose who can send you messages. "Subscribers" feature will be available soon.
                </p>
              </div>
            </div>
          </div>

          {/* Blocked Users */}
          <div>
            <h4 className="text-lg font-semibold text-gray-800 mb-3">Blocked Users</h4>
            {settings.blockedUsers && settings.blockedUsers.length > 0 ? (
              <div className="space-y-2">
                {settings.blockedUsers.map((blockedUserId) => (
                  <div key={blockedUserId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-700">User ID: {blockedUserId}</span>
                    <button
                      onClick={() => handleBlockUser(blockedUserId)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Unblock
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No blocked users</p>
            )}
          </div>

          {/* Report Feature */}
          <div>
            <h4 className="text-lg font-semibold text-gray-800 mb-3">Report User</h4>
            <p className="text-sm text-gray-600 mb-3">
              If you encounter inappropriate behavior, you can report users.
            </p>
            <button
              onClick={() => {
                const userId = prompt('Enter user ID to report:');
                if (userId) {
                  const reason = prompt('Reason for reporting:');
                  if (reason) {
                    handleReportUser(userId, reason);
                  }
                }
              }}
              className="w-full bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg font-medium transition-colors"
            >
              Report User
            </button>
          </div>

          {/* Account Info */}
          <div>
            <h4 className="text-lg font-semibold text-gray-800 mb-3">Account Information</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="text-gray-800">{userProfile?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Channel ID:</span>
                <span className="text-gray-800 font-mono text-xs">
                  {userProfile?.channelId?.substring(0, 20)}...
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Member since:</span>
                <span className="text-gray-800">
                  {userProfile?.createdAt ? new Date(userProfile.createdAt).toLocaleDateString() : 'Unknown'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdateSettings}
            disabled={loading}
            className="flex-1 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-400 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
